import CalendarClockIcon from '@material-symbols/svg-300/outlined/calendar_clock.svg'
import ScheduleSendIcon from '@material-symbols/svg-300/outlined/schedule_send.svg'
import VolunteerActivismIcon from '@material-symbols/svg-300/outlined/volunteer_activism.svg'
import classnames from 'classnames'
import { isBefore } from 'date-fns'
import { FormikProvider, useFormik } from 'formik'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { AnonymousLearningItem } from '../../../models/AnonymousLearningItem'
import { ScheduledCourseEventStatus } from '../../../models/BaseModels'
import {
  managerCanAssignLearningItemsToGroupMember,
  userCanAssignLearningItems,
  userCanBookLearningItemAfterItHasStarted,
} from '../../../state/calculations/permissions/userCan'
import { useLearningItems, useUser } from '../../../state/entities'
import useAddUsersToLearningItems from '../../../state/mutations/useAddUsersToLearningItems'
import useBookScheduledCourseEvent from '../../../state/mutations/useBookScheduledCourseEvent'
import { useSession } from '../../../state/session'
import useFocusInputWithError from '../../../utilities/useFocusInputWithError'
import { CheckboxFormik } from '../../basics/Checkbox/Checkbox'
import FieldError from '../../basics/FieldError/FieldError'
import FormButtons from '../../basics/FormButtons/FormButtons'
import { TextInputFormik } from '../../basics/TextInput/TextInput'
import {
  SelectLearningItemFormik,
  SelectLearningItemPathFormik,
} from '../../formElements/SelectLearningItem/SelectLearningItem'
import { SelectUsersFormik } from '../../formElements/SelectUsers/SelectUsers'

import styles from './CreateUserLearningAssignmentsForm.module.scss'
import { useParams } from 'react-router-dom'
import useInvalidate from '../../../state/useInvalidate'

/**********************************************************************
 * CreateUserLearningAssignmentsForm
 **********************************************************************/
interface CreateUserLearningAssignmentsFormProps {
  userUuids?: string[]
  learningItemUuid?: string
  onClose: () => void
  pathsOrCourses?: boolean
}

export default React.memo(function CreateUserLearningAssignmentsForm({
  userUuids,
  learningItemUuid,
  onClose,
  pathsOrCourses,
}: CreateUserLearningAssignmentsFormProps) {
  const { t } = useTranslation()
  const { user: currentUser } = useSession()
  const invalidate = useInvalidate()

  const { uuid } = useParams()
  const { user: profileUser } = useUser(uuid)

  // Form
  const addUsersToLearningItems = useAddUsersToLearningItems()
  const bookScheduledCourseEvent = useBookScheduledCourseEvent()
  const formik = useFormik({
    initialValues: {
      learningItemUuid: learningItemUuid || '',
      userUuids: userUuids || [],
      action: 'suggest',
      dueDate: null as Date | null,
      event: '',
    },
    enableReinitialize: true,
    onSubmit: async (values, { setStatus }) => {
      setStatus()
      try {
        await addUsersToLearningItems.mutateAsync({
          userUuids: values.userUuids,
          learningItemsAssignments: [
            {
              uuid: values.learningItemUuid,
              dueDate: values.action === 'assign' ? values.dueDate || undefined : undefined,
            },
          ],
          skipNotifications: values.action === 'book',
        })

        if (values.action === 'book') {
          invalidate.useUserBookedLearningItems()

          await Promise.all(
            values.userUuids.map((uuid) =>
              bookScheduledCourseEvent.mutateAsync({ userUuid: uuid, eventUuid: values.event })
            )
          )
        }

        onClose()
      } catch (err) {
        setStatus(String(err))
      }
    },
  })

  useFocusInputWithError(formik)

  const {
    learningItems: [learningItem],
  } = useLearningItems(formik.values.learningItemUuid ? [formik.values.learningItemUuid] : [])

  const now = useMemo(() => new Date(), [])

  const mapLearningItemSuggestions = useCallback(
    (item: AnonymousLearningItem) => {
      if (
        item.status === 'published' &&
        (userCanAssignLearningItems(currentUser) ||
          managerCanAssignLearningItemsToGroupMember(currentUser?.managedGroups || [], profileUser.groups || []))
      ) {
        return { disabled: false }
      }

      return {
        disabled: true,
        info:
          item.status !== 'published'
            ? t(`learning item.status.${item.status}`)
            : t(`manage learning.learning item not allowed`),
      }
    },
    [currentUser]
  )

  return (
    <FormikProvider value={formik}>
      <form noValidate onSubmit={formik.handleSubmit} className={classnames(styles.CreateUserLearningAssignmentsForm)}>
        <header>
          <hgroup>
            <h1>{t('manage learning.assign learning items')}</h1>
          </hgroup>
        </header>

        <div>
          <h2>
            {t(
              pathsOrCourses ? 'manage learning.step select learning path' : 'manage learning.step select learning item'
            )}
          </h2>

          {!pathsOrCourses && (
            <SelectLearningItemFormik name="learningItemUuid" required mapSuggestion={mapLearningItemSuggestions} />
          )}
          {pathsOrCourses && (
            <SelectLearningItemPathFormik name="learningItemUuid" required mapSuggestion={mapLearningItemSuggestions} />
          )}
        </div>

        <div>
          <h2>{t('manage learning.step select users')}</h2>

          <SelectUsersFormik name="userUuids" required label={t('search users')} />
        </div>

        <div>
          <h2>{t('manage learning.step select action')}</h2>

          <div className={styles.selectAction}>
            <p>{t('manage learning.description select action')}</p>
            <div className={styles.options}>
              <div>
                <CheckboxFormik
                  id="action-suggest"
                  plain
                  type="radio"
                  required
                  name="action"
                  value="suggest"
                  label={t('manage learning.action suggest')}
                />
                <label htmlFor="action-suggest">
                  <VolunteerActivismIcon />
                  {t('manage learning.action suggest')}
                </label>
              </div>
              <div>
                <CheckboxFormik
                  id="action-assign"
                  plain
                  type="radio"
                  required
                  name="action"
                  value="assign"
                  label={t('manage learning.action assign')}
                  omitErrorDisplay
                />
                <label htmlFor="action-assign">
                  <ScheduleSendIcon />
                  {t('manage learning.action assign')}
                </label>
              </div>
              <div>
                {learningItem?.kind === 'scheduledCourse' && (
                  <CheckboxFormik
                    id="action-book"
                    plain
                    type="radio"
                    required
                    name="action"
                    value="book"
                    label={t('manage learning.action book')}
                    omitErrorDisplay
                  />
                )}
                <label
                  htmlFor="action-book"
                  className={classnames({ [styles.disabled]: learningItem?.kind !== 'scheduledCourse' })}
                >
                  <CalendarClockIcon />
                  {t('manage learning.action book')}
                </label>
              </div>
            </div>
            {formik.values.action === 'suggest' && (
              <p>{t('manage learning.description suggest learning item for user')}</p>
            )}
            {formik.values.action === 'assign' && (
              <p>{t('manage learning.description assign learning item to user')}</p>
            )}
            {formik.values.action === 'book' && <p>{t('manage learning.description book learning item for user')}</p>}
            <FieldError error={formik.errors.action} touched={formik.touched.action} />
          </div>
        </div>

        {formik.values.action === 'assign' && (
          <div className={styles.dueDate}>
            <h2>{t('manage learning.step set due date')}</h2>
            <TextInputFormik name="dueDate" label={t('learning item.due date')} type="date" required />
          </div>
        )}

        {learningItem?.kind === 'scheduledCourse' && (
          <div className={styles.availableEvents}>
            {formik.values.action === 'book' && (
              <>
                <h2>{t('manage learning.step select date')}</h2>
                <div className={styles.eventGrid}>
                  {learningItem.scheduledCourse.events.map((event) => (
                    <div key={event.uuid}>
                      <CheckboxFormik
                        plain
                        type="radio"
                        name="event"
                        value={event.uuid}
                        label={t('format.DATERANGE', { range: { start: event.startDate, end: event.endDate } })}
                        id={event.uuid}
                        disabled={
                          (isBefore(event.startDate, now) &&
                            !userCanBookLearningItemAfterItHasStarted(currentUser, learningItem)) ||
                          event.status !== ScheduledCourseEventStatus.Bookable ||
                          event.seatsAvailable < formik.values.userUuids.length
                        }
                        required
                      />
                      <label htmlFor={event.uuid}>
                        <span>{t('format.DATERANGE', { range: { start: event.startDate, end: event.endDate } })}</span>
                        <span>
                          {t('learning item.{count} scheduled course event seats available', {
                            count: event.seatsAvailable,
                          })}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
                <div className={styles.errorMessage}>{formik.touched.event && formik.errors.event}</div>
              </>
            )}
            {formik.values.action !== 'book' && (
              <>
                <h2>{t('manage learning.available dates')}</h2>
                <div className={styles.eventGrid}>
                  {learningItem.scheduledCourse.events.map((event) => (
                    <div
                      key={event.uuid}
                      className={classnames({
                        [styles.unavailable]:
                          isBefore(event.startDate, now) ||
                          event.status !== ScheduledCourseEventStatus.Bookable ||
                          event.seatsAvailable < formik.values.userUuids.length,
                      })}
                    >
                      <div>
                        <span>{t('format.DATERANGE', { range: { start: event.startDate, end: event.endDate } })}</span>
                        <span>
                          {t('learning item.{count} scheduled course event seats available', {
                            count: event.seatsAvailable,
                          })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <FormButtons onClose={onClose} {...formik} isValid={true} />
      </form>
    </FormikProvider>
  )
})
